<script setup lang="ts">
import type { AgentContactBarEmits, AgentContactBarProps } from '.';

import { cn } from '~/utils';

import { BrokerAvatarInfo } from '~/components/BrokerAvatarInfo';

import IconMaterialSymbolsLightChatBubbleOutlineSharp from '~icons/material-symbols-light/chat-bubble-outline-sharp';

import messages from './AgentContactBar.locale.json';

const props = defineProps<AgentContactBarProps>();

const emit = defineEmits<AgentContactBarEmits>();

const strings = messages.pt;

const agentFirstName = computed(() => props.agentName?.split(' ')[0] ?? '');

const ctaText = computed(() =>
	strings.cta.replace('{agentFirstName}', agentFirstName.value)
);
</script>

<template>
	<!--
		Mesma moldura da barra fixa da PDP (FixedFooterContact): ClientOnly +
		`fixed bottom-0 z-40 lg:hidden`. Sem favorito, sem tag de exclusivo e sem
		as animações de entrada, que lá existem só por causa do botão de favoritar.
	-->
	<ClientOnly>
		<div
			:class="
				cn(
					'fixed inset-x-0 bottom-0 z-40 h-[60px] w-full bg-secondary px-4 py-2 lg:hidden',
					props.class
				)
			"
			data-test-id="agent-contact-bar"
		>
			<div class="flex w-full items-center">
				<!--
					`hide-info` porque o design da barra do corretor não mostra nome nem
					imobiliária em nenhuma largura de mobile.

					w-[68px] vem da conta do Figma: barra de 375px menos 32px de padding
					dá 343px úteis; o botão mede 275px, logo sobram 68px para o avatar. O
					avatar tem 36px e o badge `linear` o estende até 60px, deixando 8px
					de respiro antes do botão.
					Por isso a linha não tem gap: a folga já está embutida nos 68px.
				-->
				<BrokerAvatarInfo
					:border="false"
					avatar-shape="circle"
					company-avatar-shape="circle"
					:show-company-avatar="true"
					sizes="linear"
					hide-info
					:broker-src="agentPictureSrc || ''"
					:company-src="companyLogoSrc || ''"
					avatar-class="size-9"
					:broker-name="agentName"
					:company-name="companyName"
					class="w-[68px] shrink-0"
				/>

				<!--
					O Button renderiza o <button> dentro de um wrapper <Skeleton>, e é o
					wrapper que é o item flex desta linha. Por isso o flex-1 vai em
					`skeleton-classes`: em `class` ele cairia no elemento interno e o
					wrapper continuaria encolhendo no tamanho do conteúdo.
				-->
				<Button
					data-test-id="agent-contact-bar-button"
					:is-client-only="false"
					:is-skeleton-loading="false"
					skeleton-classes="flex-1"
					class="flex h-11 w-full items-center justify-center gap-2 rounded-[2px] bg-primary px-4 py-2 text-sm font-medium leading-5 text-white"
					@click="emit('contactClick')"
				>
					<IconMaterialSymbolsLightChatBubbleOutlineSharp
						class="size-5 text-beige"
					/>
					{{ ctaText }}
				</Button>
			</div>
		</div>
	</ClientOnly>
</template>
